'use client';

import React, { type FC, type ReactNode, useMemo } from 'react';

export interface FormattedPriceProps {
  /** Pre-formatted price string from formatMoney, e.g. "$111.22", "€1.234,56" */
  formattedPrice: string;
  /** Font size for the integer/currency part (px) */
  integerFontSize?: number;
  /** Font size for the decimal separator + fraction part (px) */
  fractionFontSize?: number;
  integerFontWeight?: number;
  fractionFontWeight?: number;
  postfix?: ReactNode;
}

/**
 * Splits a formatted price string into integer and fraction parts,
 * rendering each with a different font size.
 *
 * Detects the last decimal separator (`.` or `,` followed by exactly 2 digits at the end).
 */
export const FormattedPrice: FC<FormattedPriceProps> = ({
  postfix,
  formattedPrice,
  integerFontSize = 28,
  integerFontWeight = 700,
  fractionFontSize = 16,
  fractionFontWeight = 700,
}) => {
  const { integerPart, fractionPart } = useMemo(() => {
    // Match the last separator (. or ,) followed by exactly 2 digits at the end
    const match = formattedPrice.match(/^(.+)([.,]\d{2})$/);

    if (!match) {
      return { integerPart: formattedPrice, fractionPart: '' };
    }

    return { integerPart: match[1], fractionPart: match[2] };
  }, [formattedPrice]);

  return (
    <span style={{ lineHeight: 1, whiteSpace: 'nowrap' }}>
      <span
        style={{ fontSize: integerFontSize, fontWeight: integerFontWeight }}
      >
        {integerPart}
      </span>
      {fractionPart && (
        <span
          style={{ fontSize: fractionFontSize, fontWeight: fractionFontWeight }}
        >
          {fractionPart}
        </span>
      )}
      {postfix && (
        <span
          style={{ fontSize: fractionFontSize, fontWeight: fractionFontWeight }}
        >
          {postfix}
        </span>
      )}
    </span>
  );
};
